import type { TCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useStudentFeeSimpleStore } from '@/stores/studentFeeSimple'
import { useWordwareLunchStore } from '@/stores/wordwareLunch'
import { useTuitionContractStore } from '@/stores/tuitionContract'
import { useAffinetyActivityFeesStore } from '@/stores/affinetyActivityFees'
import { useAffinetyActivityStore } from '@/stores/affinetyActivities'
import { useMbaPowerschoolFeeStore } from '@/stores/mbaPowerschoolFees'
import { useCorePowerschoolFeeStore } from '@/stores/corePowerschoolFees'
import { useMbaCafeLunchStore } from '@/stores/mbaCafeLunch'
import { useFamilyUserInvoiceStore } from '@/stores/familyUserInvoice'
import { useStore } from '@/stores'
import { API } from '@/modules/api'
import PaymentFormatter from '@/classes/PaymentFormatter'

export const useCartStore = defineStore('cart', {
  state: () => ({
    cartLines: ref<Record<TDistrictId, Record<string, TCartLine>>>({}),
    orderEstimates: ref<Record<TDistrictId, any[]>>({}),
    orderEstimatesLoading: ref<Record<TDistrictId, boolean>>({}),
    selectedFundingSourceId: ref<Record<TDistrictId, number | null>>({}),
    processingFees: ref<Record<TDistrictId, TCartLine[]>>({})
  }),
  persist: true,
  getters: {
    getCartLines(state) {
      // const studentFeeSimpleStore = useStudentFeeSimpleStore()
      // const tuitionContractStore = useTuitionContractStore()
      // const affinetyActivityStore = useAffinetyActivityFeesStore()
      // const activityStore = useAffinetyActivityStore()
      // const mbaPowerschoolFeeStore = useMbaPowerschoolFeeStore()
      // const corePowerschoolFeeStore = useCorePowerschoolFeeStore()
      // const mbaCafeLunch = useMbaCafeLunchStore()
      // const familyUserInvoiceStore = useFamilyUserInvoiceStore()
      return (districtId: TDistrictId) => [
        ...Object.values(state.cartLines[districtId] ?? {})
        // ...studentFeeSimpleStore.getCartLinesForDistrict(districtId),
        // ...tuitionContractStore.getCartLinesForDistrict(districtId),
        // ...affinetyActivityStore.getCartLinesForDistrict(districtId),
        // ...activityStore.getCartLinesForDistrict(districtId),
        // ...mbaPowerschoolFeeStore.getCartLinesForDistrict(districtId),
        // ...corePowerschoolFeeStore.getCartLinesForDistrict(districtId),
        // ...mbaCafeLunch.getCartLinesForDistrict(districtId),
        // ...familyUserInvoiceStore.getCartLinesForDistrict(districtId),
        // ...(state.processingFees[districtId] || [])
      ]
    },
    getCartLine(state) {
      return <T extends TCartLine>(districtId: TDistrictId, uniqueId: string | number): T | null =>
        (state.cartLines?.[districtId]?.[uniqueId] as T) ?? null
    },
    getSubtotal(state) {
      return (districtId: TDistrictId) =>
        this.getCartLines(districtId).reduce((acc, line) => acc + line.amount, 0)
    },
    getProcessingFeesTotal(state) {
      return (districtId: TDistrictId) =>
        (state.processingFees[districtId] || []).reduce((acc, fee) => acc + fee.amount, 0)
    },
    getCartTotal(state) {
      return (districtId: TDistrictId) =>
        this.getSubtotal(districtId) + this.getProcessingFeesTotal(districtId)
    },
    getSelectedFundingSource(state) {
      return (districtId: TDistrictId) => {
        const fundingSources = useStore().getFundingSources(districtId)
        if (!state.selectedFundingSourceId[districtId]) {
          return fundingSources.length > 0 ? fundingSources[0] : null
        }
        const selectedFsId = state.selectedFundingSourceId[districtId]
        return fundingSources.find(fs => fs.fundingSourceId === selectedFsId) ?? null
      }
    },
    getOrderEstimate(state) {
      return (districtId: TDistrictId) => state.orderEstimates[districtId] ?? null
    },
    getOrderEstimateLoading(state) {
      return (districtId: TDistrictId) => state.orderEstimatesLoading[districtId] ?? false
    }
  },
  actions: {
    clear() {
      this.cartLines = {}
      this.orderEstimates = {}
      this.orderEstimatesLoading = {}
      this.selectedFundingSourceId = {}
      this.processingFees = {}
    },
    clearCart(districtId: TDistrictId) {
      this.cartLines[districtId] = {}
      this.orderEstimates[districtId] = []
      this.orderEstimatesLoading[districtId] = false
    },
    checkoutDistrictCart(districtId: TDistrictId) {
      const all = [...this.getCartLines(districtId), ...this.processingFees[districtId]]
      // ...
    },
    async fetchOrderEstimate(districtId: TDistrictId) {
      // A district with no supported merchant account has no checkout/processing-fee
      // config, so the estimate endpoints 500 — and there is nothing to estimate.
      // Skip it (DTB-343).
      if (useStore().getDistrict(districtId)?.acceptingPayments === false) {
        this.orderEstimates[districtId] = []
        this.orderEstimatesLoading[districtId] = false
        return
      }
      this.orderEstimatesLoading[districtId] = true
      try {
        const lines = this.getCartLines(districtId)
        const payments: Record<string, TFormattedPayment[]> = PaymentFormatter.format(lines)
        const fundingSource = this.getSelectedFundingSource(districtId)
        let orderEstimate = null
        // TODO it would be nice to combine these two calls into one
        if (fundingSource) {
          orderEstimate = await API.Post.orderEstimate(districtId, {
            payments,
            fundingSourceId: fundingSource.fundingSourceId
          })
        } else {
          orderEstimate = await API.Post.orderEstimateWithoutFS(districtId, {
            payments
          })
        }
        this.orderEstimates[districtId] = orderEstimate
      } catch (error) {
        console.error('Failed to fetch order estimate:', error)
      } finally {
        this.orderEstimatesLoading[districtId] = false
      }
    },
    addCartLine(districtId: TDistrictId, uniqueId: string | number, line: TCartLine) {
      if (!this.cartLines[districtId]) {
        this.cartLines[districtId] = {}
      }
      this.cartLines[districtId][uniqueId] = line
      this.fetchOrderEstimate(districtId)
    },
    updateCartLine(districtId: TDistrictId, uniqueId: string, line: Partial<TCartLine>) {
      this.cartLines[districtId][uniqueId] = {
        ...(this.cartLines[districtId][uniqueId] ?? {}),
        ...line
      }
      this.fetchOrderEstimate(districtId)
    },
    removeCartLine(districtId: TDistrictId, uniqueId: string) {
      if (!this.cartLines[districtId]) return
      delete this.cartLines[districtId][uniqueId]
      this.fetchOrderEstimate(districtId)
    },
    selectFundingSource(districtId: TDistrictId, fundingSourceId: number) {
      this.selectedFundingSourceId[districtId] = fundingSourceId
      // Mark the estimate stale in the same tick as the pick. The refetch is
      // debounced, so without this the old card's estimate stays on screen
      // with Pay Now enabled until the debounce fires.
      this.orderEstimatesLoading[districtId] = true
      this.fetchOrderEstimate(districtId)
    }
  },
  debounce: {
    fetchOrderEstimate: 50 // Make sure if many items added at once, it doesn't send a ton of requests
  }
})
